import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { Arrows, Comment, Quotation, Tag, Plus, Video } from '@bibliocommons/deprecated-base-icons';
import { Dropdown } from '@bibliocommons/deprecated-base-dropdown';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { UGC_CONTENT_TYPES } from '@bibliocommons/constants-ugc';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';

import {
  bibShape,
  userIdentityShape,
  ugcCommentShape,
  ugcTagShape,
  ugcVideoShape,
  ugcPrivateNoteShape,
  ugcContentAdvisoryShape,
  ugcAgeSuitabilityShape,
  ugcSummaryShape
} from '@bibliocommons/bc-prop-types';

import FullScreenOverlay from 'app/components/deprecated/shared/FullScreenOverlay';
import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import SimilarTitlesUGCEditor from 'app/components/shared/UGC/UGCEditors/SimilarTitlesUGCEditor';
import CommentUGCEditor from 'app/components/shared/UGC/UGCEditors/CommentUGCEditor';
import QuotationUGCEditor from 'app/components/shared/UGC/UGCEditors/QuotationUGCEditor';
import TagsUGCEditor from 'app/components/shared/UGC/UGCEditors/TagsUGCEditor';
import VideoUGCEditor from 'app/components/shared/UGC/UGCEditors/VideoUGCEditor';
import PrivateNoteUGCEditor from 'app/components/shared/UGC/UGCEditors/PrivateNoteUGCEditor';
import ContentAdvisoryUGCEditor, {
  contentAdvisoryTypes
} from 'app/components/shared/UGC/UGCEditors/ContentAdvisoryUGCEditor';
import AgeSuitabilityUGCEditor from 'app/components/shared/UGC/UGCEditors/AgeSuitabilityUGCEditor';
import SummaryUGCEditor from 'app/components/shared/UGC/UGCEditors/SummaryUGCEditor';
import { AddTitleOverlay } from 'app/components/shared/AddTitle';

import './Toolbar.scss';

export default class ShelfListItemToolbar extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      ugcContentType: null
    };
  }

  componentDidUpdate(prevProps) {
    if (this.ugcIsPresent()) {
      return;
    }
    // Focus the "Add comment" button if it is displayed after being hidden
    if (this.commentButtonRef && prevProps.ownerCommentForBib) {
      setFocusWithinElement(this.commentButtonRef);
    }
  }

  ugcIsPresent() {
    return [
      'ownerQuotationsForBib',
      'ownerTagsForBib',
      'ownerVideoForBib',
      'ownerPrivateNoteForBib',
      'ownerContentAdvisoriesForBib',
      'ownerAgeSuitabilityForBib',
      'ownerSummaryForBib',
      'similarTitleBibs'
    ].some(type => !!this.props[type] && !this.props[type].isEmpty());
  }

  openUGCEditor(ugcContentType) {
    this.setState({ ugcContentType });
  }

  closeUGCEditor() {
    this.setState({ ugcContentType: null });
  }

  filterOption(option) {
    switch (option) {
      case UGC_CONTENT_TYPES.VIDEO:
        return !this.props.ownerVideoForBib;
      case UGC_CONTENT_TYPES.PRIVATE_NOTE:
        return !this.props.ownerPrivateNoteForBib;
      case UGC_CONTENT_TYPES.CONTENT_ADVISORIES:
        return (
          !this.props.ownerContentAdvisoriesForBib ||
          this.props.ownerContentAdvisoriesForBib.size < contentAdvisoryTypes.length
        );
      case UGC_CONTENT_TYPES.AGE_SUITABILITY:
        return !this.props.ownerAgeSuitabilityForBib;
      case UGC_CONTENT_TYPES.SUMMARY:
        return !this.props.ownerSummaryForBib;
      default:
        return true;
    }
  }

  renderCommentButton() {
    if (this.props.ownerCommentForBib) {
      return null;
    }

    return (
      <TextButton
        className="shelf-ugc-comment-button"
        dataKey="add-comment-button"
        handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.COMMENT)}
        ref={el => {
          this.commentButtonRef = el;
        }}
      >
        <span aria-hidden>
          <Comment className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_comment_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_comment" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderTagsButton() {
    return (
      <TextButton
        className="shelf-ugc-tags-button"
        dataKey="add-tags"
        handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.TAGS)}
      >
        <span aria-hidden>
          <Tag color="currentColor" className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_tags_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_tags" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderQuotationButton() {
    return (
      <TextButton
        className="shelf-ugc-quotation-button"
        dataKey="add-quotations"
        handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.QUOTATIONS)}
      >
        <span aria-hidden>
          <Quotation className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_quotations_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_quotations" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderVideoButton() {
    if (this.props.ownerVideoForBib) {
      return null;
    }

    return (
      <TextButton className="shelf-ugc-video-button" handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.VIDEO)}>
        <span aria-hidden>
          <Video className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_video_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_video" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderAddMoreDropdown() {
    const renderAddMoreButton = () => (
      <span className="shelf-ugc-add-more-button">
        <span aria-hidden>
          <Plus className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_more_dropdown_label" />
          <Arrows.Medium.Down className="selected-icon extra-small" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_more_ugc" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </span>
    );

    const options = [
      UGC_CONTENT_TYPES.VIDEO,
      UGC_CONTENT_TYPES.SIMILAR_TITLES,
      UGC_CONTENT_TYPES.PRIVATE_NOTE,
      UGC_CONTENT_TYPES.CONTENT_ADVISORIES,
      UGC_CONTENT_TYPES.AGE_SUITABILITY,
      UGC_CONTENT_TYPES.SUMMARY
    ]
      .filter(option => this.filterOption(option))
      .map(type => ({
        value: type,
        label: <FormattedMessage id={`ugc_${type}`} />
      }));

    return (
      <Dropdown
        disableMobile
        dataKey="shelf-list-item-toolbar-dropdown"
        className="shelf-ugc-add-more-dropdown"
        ButtonTag={TextButton}
        options={options}
        handleSelect={this.openUGCEditor}
        renderSelected={renderAddMoreButton}
      />
    );
  }

  renderUGCEditor() {
    switch (this.state.ugcContentType) {
      case UGC_CONTENT_TYPES.TAGS: {
        return (
          <TagsUGCEditor
            bib={this.props.bib}
            owner={this.props.owner}
            existingTags={this.props.ownerTagsForBib}
            tagsActions={this.props.tagsActions}
            tagsTransactions={this.props.tagsTransactions}
            handleClose={this.closeUGCEditor}
          />
        );
      }

      case UGC_CONTENT_TYPES.COMMENT: {
        return (
          <CommentUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            commentsTransactions={this.props.commentsTransactions}
            shelfTransactions={this.props.shelfTransactions}
            commentsActions={this.props.commentsActions}
            shelfActions={this.props.shelfActions}
          />
        );
      }

      case UGC_CONTENT_TYPES.QUOTATIONS: {
        return (
          <QuotationUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            quotationsTransactions={this.props.quotationsTransactions}
            shelfTransactions={this.props.shelfTransactions}
            quotationsActions={this.props.quotationsActions}
            shelfActions={this.props.shelfActions}
          />
        );
      }

      case UGC_CONTENT_TYPES.VIDEO: {
        return (
          <VideoUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            videosTransactions={this.props.videosTransactions}
            shelfTransactions={this.props.shelfTransactions}
            videosActions={this.props.videosActions}
            shelfActions={this.props.shelfActions}
          />
        );
      }

      case UGC_CONTENT_TYPES.PRIVATE_NOTE: {
        return (
          <PrivateNoteUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            privateNotesTransactions={this.props.privateNotesTransactions}
            shelfTransactions={this.props.shelfTransactions}
            privateNotesActions={this.props.privateNotesActions}
            shelfActions={this.props.shelfActions}
          />
        );
      }

      case UGC_CONTENT_TYPES.CONTENT_ADVISORIES: {
        return (
          <ContentAdvisoryUGCEditor
            bib={this.props.bib}
            existingAdvisories={this.props.ownerContentAdvisoriesForBib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            contentAdvisoriesActions={this.props.contentAdvisoriesActions}
            contentAdvisoriesTransactions={this.props.contentAdvisoriesTransactions}
            shelfActions={this.props.shelfActions}
            shelfTransactions={this.props.shelfTransactions}
          />
        );
      }

      case UGC_CONTENT_TYPES.AGE_SUITABILITY: {
        return (
          <AgeSuitabilityUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            ageSuitabilitiesActions={this.props.ageSuitabilitiesActions}
            ageSuitabilitiesTransactions={this.props.ageSuitabilitiesTransactions}
            shelfActions={this.props.shelfActions}
            shelfTransactions={this.props.shelfTransactions}
          />
        );
      }

      case UGC_CONTENT_TYPES.SUMMARY: {
        return (
          <SummaryUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            summariesActions={this.props.summariesActions}
            summariesTransactions={this.props.summariesTransactions}
            shelfActions={this.props.shelfActions}
            shelfTransactions={this.props.shelfTransactions}
          />
        );
      }

      default:
        return null;
    }
  }

  renderSimilarTitlesUGCEditor() {
    if (this.state.ugcContentType === UGC_CONTENT_TYPES.SIMILAR_TITLES) {
      return (
        <SimilarTitlesUGCEditor
          bib={this.props.bib}
          shelfItem={this.props.item}
          similarTitleBibs={this.props.similarTitleBibs}
          similarTitlesTransactions={this.props.similarTitlesTransactions}
          shelfTransactions={this.props.shelfTransactions}
          similarTitlesActions={this.props.similarTitlesActions}
          shelfActions={this.props.shelfActions}
          handleClose={this.closeUGCEditor}
        />
      );
    }

    return null;
  }

  render() {
    return (
      <div className="cp-shelf-list-item-toolbar">
        <div className="ugc-toolbar">
          <span className="add-label">
            <FormattedMessage id="ugc_toolbar_add_label" />
          </span>
          {this.renderCommentButton()}
          {this.renderTagsButton()}
          {this.renderQuotationButton()}
          {this.renderAddMoreDropdown()}
        </div>

        <AddTitleOverlay
          open={this.state.ugcContentType === UGC_CONTENT_TYPES.SIMILAR_TITLES}
          handleClose={this.closeUGCEditor}
          handleFocusOnRestore={this.props.handleUGCFocus}
        >
          {this.renderSimilarTitlesUGCEditor()}
        </AddTitleOverlay>

        <FullScreenOverlay
          open={!!this.state.ugcContentType && this.state.ugcContentType !== UGC_CONTENT_TYPES.SIMILAR_TITLES}
          handleClose={this.closeUGCEditor}
          handleFocusOnRestore={this.props.handleUGCFocus}
        >
          {this.renderUGCEditor()}
        </FullScreenOverlay>
      </div>
    );
  }
}

ShelfListItemToolbar.propTypes = {
  bib: bibShape.isRequired,
  item: shelfItemShape.isRequired,
  owner: userIdentityShape.isRequired,
  ownerCommentForBib: ugcCommentShape,
  ownerTagsForBib: ImmutablePropTypes.listOf(ugcTagShape),
  ownerVideoForBib: ugcVideoShape,
  ownerPrivateNoteForBib: ugcPrivateNoteShape,
  ownerContentAdvisoriesForBib: ImmutablePropTypes.listOf(ugcContentAdvisoryShape),
  ownerAgeSuitabilityForBib: ugcAgeSuitabilityShape,
  ownerSummaryForBib: ugcSummaryShape,
  similarTitleBibs: ImmutablePropTypes.map,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired,
  ageSuitabilitiesTransactions: ImmutablePropTypes.map.isRequired,
  summariesTransactions: ImmutablePropTypes.map.isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ageSuitabilitiesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  summariesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  handleUGCFocus: PropTypes.func.isRequired
};
